/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Form, TabContainer, Nav, Tab, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBarcode, faStar } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from 'react-query';
import classNames from 'classnames';

import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { pageToOffset } from 'utils/pageToOffset';
import reviewsService from 'service/reviews';
import useLightbox from 'hooks/useLightbox';
import Lightbox from 'components/base/LightBox';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import Rating from 'components/base/Rating';
import legalEntitySettingsService from 'service/legalEntitySettings';
import AnswerTheReview from './AnswerTheReview';

export type ReviewsProductType = {
  productId: string;
  client: string;
  rate: number;
  advantages: string;
  flaws: string;
  comments: string;
  experience_of_use: string;
  photos: string;
  created_date: string;
  employee: string;
  sellers_response: string;
  date_of_response: string;
  barcode: string;
};

export type ReviewsStoreType = {
  client: string;
  rate: any;
  comments: string;
  created_date: string;
};
const Reviews = () => {
  const { t, i18n } = useTranslation();
  const [key, setKey] = useState('product');

  const [getProductName, setProductName] = useState<any>('');
  const [getProductBarcode, setProductBarcode] = useState<any>('');
  const [productPageCount, setProductPageCount] = useState(1);
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [storePageCount, setStorePageCount] = useState(1);
  const [storeCurrentPage, setStoreCurrentPage] = useState(1);
  const [openAnswer, setOpenAnswer] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>();

  const reviewsBreadcrumbItems: PageBreadcrumbItem[] = useMemo(() => {
    return [
      {
        label: t('reviews'),
        active: true
      }
    ];
  }, [t]);

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  //Get Products
  const {
    data: dataProduct,
    refetch: refetchProduct,
    isLoading: isLoadingProduct,
    isFetching: isFetchingProduct
  } = useQuery(
    [
      'GET_LEGAL_SYSTEM_PRODUCT',
      getProductName,
      getProductBarcode,
      productCurrentPage,
      i18n?.language
    ],
    async () => {
      const res = await reviewsService
        .getListReviewsProducts({
          offset: pageToOffset(productCurrentPage, 10),
          limit: 10,
          name: getProductName,
          barcode: getProductBarcode,
          lang: i18n?.language
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!productCurrentPage ||
        !!getProductBarcode ||
        !getProductName ||
        getProductName?.length > 2
    }
  );

  useEffect(() => {
    if (dataProduct) {
      setProductPageCount(Math.ceil(dataProduct?.count / 10));
    }
  }, [dataProduct]);

  const loadingProduct = isLoadingProduct || isFetchingProduct;

  //Get Not active products
  const {
    data: dataStore,
    refetch: refetchStore,
    isLoading: isLoadingStore,
    isFetching: isFetchingStore
  } = useQuery(
    ['GET_LEGAL_SYSTEM_STORE', storeCurrentPage, i18n?.language],
    async () => {
      const res = await reviewsService
        .getListReviewsStores({
          offset: pageToOffset(storeCurrentPage, 10),
          limit: 10,
          status: false,
          lang: i18n?.language
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled: !!storeCurrentPage
    }
  );

  useEffect(() => {
    if (dataStore) {
      setStorePageCount(Math.ceil(dataStore?.count / 10));
    }
  }, [dataStore]);

  const loadingStore = isLoadingStore || isFetchingStore;

  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const productData: ReviewsProductType[] = useMemo(() => {
    return (
      dataProduct?.items?.map((items: any) => {
        let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

        timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

        return {
          photos: items?.medias?.[0]
            ? `${process.env.REACT_APP_CDN_URL}${items?.medias?.[0]?.media_url}`
            : '',
          product: items?.name || '',
          client:
            (items?.user?.first_name || '') +
            ' ' +
            (items?.user?.last_name || ''),
          rate: items?.rate || 0,
          advantages: items?.advantages || '',
          flaws: items?.disadvantages || '',
          comments: items?.comment || '',
          experience_of_use: items?.usage_period || '',
          created_date: formatDateToTimeZone(+items?.created_at, timeZone),
          employee:
            (items?.employee?.first_name || '') +
            ' ' +
            (items?.employee?.last_name || ''),
          sellers_response: items?.replied_comment || '',
          date_of_response:
            !items?.replied_at || items.replied_at === '0'
              ? ''
              : formatDateToTimeZone(+items?.replied_at, timeZone),
          barcode: items?.barcode || '',
          guid: items?.id
        };
      }) ?? []
    );
  }, [dataProduct, t, i18n?.language, dataSettings]);

  const storeData: ReviewsStoreType[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    return (
      dataStore?.items?.map((items: any) => {
        return {
          client:
            (items?.user?.first_name || '') +
            ' ' +
            (items?.user?.last_name || ''),
          rate: items?.rates || [],
          comments: items?.comment || '',
          created_date: formatDateToTimeZone(+items?.created_at, timeZone),
          guid: items?.id
        };
      }) ?? []
    );
  }, [dataStore, t, i18n?.language, dataSettings]);

  const productTableColumns: ColumnDef<ReviewsProductType>[] = useMemo(
    () => [
      {
        accessorKey: 'product',
        header: t('product'),
        meta: {
          cellProps: {
            className: 'text-900',
            style: { whiteSpace: 'nowrap' }
          }
        }
      },
      {
        accessorKey: 'client',
        header: t('client'),
        meta: {
          cellProps: {
            className: 'text-900',
            style: { whiteSpace: 'nowrap' }
          }
        }
      },

      {
        accessorKey: 'rate',
        header: t('rate'),
        cell: ({ row: { original } }) => {
          const { rate } = original;
          return <Rating iconClass="fs-10" readonly initialValue={rate} />;
        },
        meta: {
          headerProps: { style: { minWidth: 110 } }
        }
      },
      {
        accessorKey: 'advantages',
        header: t('advantages'),
        meta: {
          cellProps: {
            className: 'text-900',
            style: { whiteSpace: 'nowrap' }
          }
        }
      },
      {
        accessorKey: 'flaws',
        header: t('flaws'),
        meta: {
          cellProps: {
            className: 'text-900',
            style: { whiteSpace: 'nowrap' }
          }
        }
      },
      {
        accessorKey: 'comments',
        header: t('comments'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'experience_of_use',
        header: t('experience_of_use'),
        cell: ({ row: { original } }) => {
          const { experience_of_use } = original;

          const experienceMap: Record<string, string> = {
            DAY: t('DAY'),
            WEEK: t('WEEK'),
            MONTH: t('MONTH'),
            LESS_MONTH: t('LESS_MONTH'),
            SOME_MONTHS: t('SOME_MONTHS'),
            SOME_YEARS: t('SOME_YEARS')
          };

          return <div>{experienceMap[experience_of_use] || ''}</div>;
        },
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'photo',
        header: t('photo'),
        cell: rowData => {
          const [attachments] = useState([rowData?.row?.original.photos]);
          const { lightboxProps, openLightbox } = useLightbox(attachments);

          return (
            <>
              {rowData?.row?.original.photos && (
                <div>
                  <Lightbox {...lightboxProps} />

                  {attachments.map((img, index) => (
                    <img
                      key={img}
                      style={{
                        width: '35px',
                        height: '35px',
                        border: '1px solid #8200BF',
                        borderRadius: '100%',
                        cursor: 'pointer'
                      }}
                      src={img}
                      alt=""
                      onClick={() => openLightbox(index + 1)}
                    />
                  ))}
                </div>
              )}
            </>
          );
        },

        meta: {
          cellProps: { className: 'text-900 text-center' }
        }
      },
      {
        accessorKey: 'created_date',
        header: t('created_date'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },
      {
        accessorKey: 'employee',
        header: t('employee'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },

      {
        accessorKey: 'sellers_response',
        header: t('sellers_response'),
        cell: ({ row: { original } }) => {
          const { sellers_response } = original;
          return (
            <div>
              {sellers_response !== '' ? (
                <span>{sellers_response}</span>
              ) : (
                <Button
                  variant="soft-primary"
                  onClick={() => {
                    setOpenAnswer(true);
                    setSelectedRow(original);
                  }}
                >
                  {t('answer')}
                </Button>
              )}
            </div>
          );
        },
        meta: {
          cellProps: {
            className: 'text-900',
            style: { whiteSpace: 'nowrap' }
          }
        }
      },
      {
        accessorKey: 'date_of_response',
        header: t('date_of_response'),
        meta: {
          cellProps: { className: 'text-900' }
        }
      },

      {
        accessorKey: 'barcode',
        header: t('barcode'),
        meta: {
          cellProps: {
            className: 'text-900',
            style: { whiteSpace: 'nowrap' }
          }
        }
      }
    ],
    [t]
  );

  const storeTableColumns: ColumnDef<ReviewsStoreType>[] = useMemo(
    () => [
      {
        accessorKey: 'client',
        header: t('client'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'rate',
        header: t('rate'),
        cell: ({ row: { original } }) => {
          const { rate } = original;
          return (
            <div className="d-flex flex-column">
              {rate?.map((item: any) => (
                <div key={item?.id} className="d-flex gap-2">
                  <FontAwesomeIcon
                    icon={faStar}
                    className={classNames(`text-warning`)}
                  />
                  <span>{item?.rating}</span>
                  <span>{item?.rating_criteria}</span>
                </div>
              ))}
            </div>
          );
        },
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'comments',
        header: t('comments'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      },
      {
        accessorKey: 'created_date',
        header: t('created_date'),
        meta: {
          cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
        }
      }
    ],
    [t]
  );

  const tableProduct = useAdvanceTable({
    data: productData,
    columns: productTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const tableStore = useAdvanceTable({
    data: storeData,
    columns: storeTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <PageBreadcrumb items={reviewsBreadcrumbItems} />
      <div className="mb-5">
        <h2>{t('reviews')}</h2>
      </div>

      <TabContainer
        activeKey={key}
        onSelect={(k: string | null) => setKey(k ?? 'product')}
      >
        <Nav variant="underline" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="product">{t('products')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="store">{t('store')}</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="product">
            <div className="d-flex gap-2 mb-3">
              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    type="text"
                    placeholder={t('enter_product')}
                    onFocus={e => e.target.select()}
                    onChange={e => setProductName(e.target.value)}
                    className="form-icon-input"
                  />
                  <label
                    htmlFor="floatingInputCustom"
                    className="form-icon-label text-700"
                  >
                    {t('enter_product')}
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-900 fs-9 form-icon"
                />
              </div>

              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    type="text"
                    placeholder={t('scan_barcode')}
                    onFocus={e => e.target.select()}
                    onChange={e => setProductBarcode(e.target.value)}
                    className="form-icon-input"
                  />
                  <label
                    htmlFor="floatingInputCustom"
                    className="form-icon-label text-700"
                  >
                    {t('scan_barcode')}
                  </label>
                </Form.Floating>
                <FontAwesomeIcon
                  icon={faBarcode}
                  className="text-900 fs-9 form-icon"
                />
              </div>
            </div>
            <AdvanceTableProvider {...tableProduct}>
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table fs-9' }}
                  isLoading={loadingProduct}
                />
                <AdvanceTableCPagination
                  count={productPageCount}
                  page={productCurrentPage}
                  setCurrentPage={setProductCurrentPage}
                />
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
          <Tab.Pane eventKey="store">
            <AdvanceTableProvider {...tableStore}>
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
                <AdvanceTable
                  tableProps={{ className: 'phoenix-table fs-9' }}
                  isLoading={loadingStore}
                />
                <AdvanceTableCPagination
                  count={storePageCount}
                  page={storeCurrentPage}
                  setCurrentPage={setStoreCurrentPage}
                />
              </div>
            </AdvanceTableProvider>
          </Tab.Pane>
        </Tab.Content>
      </TabContainer>

      <Offcanvas
        show={openAnswer}
        onHide={() => {
          setOpenAnswer(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('answer')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AnswerTheReview
            selectedRow={selectedRow}
            refetchProduct={refetchProduct}
            refetchStore={refetchStore}
            setOpenAnswer={setOpenAnswer}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Reviews;
